import { useColumnCount } from "@redux/slices/globalSlice";
import { ProductCategory, ProductTypeCommon, ViewType } from "@utils/constants";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import {
  getRequestType,
  RequestTypes,
  showRequestItemFromSupplier,
} from "@scenes/requests/utils/utils";

export const calculateEmptyWidth = ({
  isTablet,
  showCheckbox,
  showFavorite,
  useMobileWebView,
  fromRequestDetail,
}: any) => {
  let width: number = 32; //padding on both sides
  if (isTablet) {
    width = showCheckbox
      ? width - 32 // due to padding of checkbox in header
      : width + 30; // checkbox width including its space
  } else {
    width += 22; // checkbox width
  }
  if (showFavorite) width += isTablet ? 44 : 32; // favorite icon + space between checkbox and fav
  if (useMobileWebView) {
    width += 16;
  }
  if (fromRequestDetail) {
    width += 4;
  }
  return width;
};

export const useSectionColumnCount = (
  sectionName: any,
  productType: string,
  subType?: string
) => {
  const section =
    productType === ProductCategory.JEWELRY
      ? subType === ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
        ? "labgrown_jewelry"
        : "jewelry"
      : sectionName;

  const columnCountObject = useColumnCount();
  const mobileColumnCount =
    columnCountObject?.[productType]?.[section]?.mobile ?? 2;
  const webColumnCount = columnCountObject?.[productType]?.[section]?.web ?? 2;

  return {
    mobileColumnCount,
    webColumnCount,
  };
};

export const hasUpdatedPrice = (mItem: any) => {
  const hasUpdatedPricePerCarat = mItem?.updated_price_per_carat
    ? Number(mItem?.updated_price_per_carat) !== Number(mItem?.price_per_carat)
    : false;
  const hasUpdatedTotalSalesPrice = mItem?.updated_total_sales_price
    ? Number(mItem?.updated_total_sales_price) !==
      Number(mItem?.total_sales_price)
    : false;
  const hasUpdatedDiscountPercent = mItem?.updated_discount_percent
    ? Number(mItem?.updated_discount_percent) !==
      Number(mItem?.discount_percent)
    : false;
  return (
    hasUpdatedPricePerCarat ||
    hasUpdatedTotalSalesPrice ||
    hasUpdatedDiscountPercent
  );
};

const DEFAULT_OFFER_KEYS = [
  "offer_price_per_carat",
  "offer_total_sales_price",
  "offer_discount_percent",
];

const EXTRA_PRICE_KEYS = [
  "price_per_carat",
  "TotalPrice",
  "total_sales_price",
  "discount_percent",
];

export interface VisibleColumnsParams {
  request_type?: string;
  hasAnyOfferDetails?: boolean;
  viewType?: string;
  showListPrice?: boolean;
  isTotalOfferDiscountApplied?: boolean;
  isTotalOfferPriceApply?: boolean;
  sectionName?: string;
  stockItemRequest?: any;
}

// Mirrors the keysToRemove logic in tabletListHeader.tsx. Keep these in sync
// (the header imports getKeysToRemove from this file).
export const getKeysToRemove = ({
  request_type,
  hasAnyOfferDetails,
  viewType,
  showListPrice,
  isTotalOfferDiscountApplied,
  isTotalOfferPriceApply,
  sectionName,
  stockItemRequest,
}: VisibleColumnsParams): string[] => {
  const viewTypeSafe = viewType ?? "";
  const hideTotalPrice =
    getRequestType(viewTypeSafe) !== RequestTypes.Quote
      ? true
      : request_type === ViewType.BUYER ||
        !!showListPrice ||
        !!isTotalOfferDiscountApplied;

  let keysToRemove: string[] = [];

  if (request_type === "buyer_view" && hasAnyOfferDetails) {
    keysToRemove = [];
  } else if (request_type === "seller_view" && hasAnyOfferDetails) {
    if (getRequestType(viewTypeSafe) === RequestTypes.Quote) {
      keysToRemove = [...EXTRA_PRICE_KEYS];
    }
  } else if (
    request_type === "seller_view" &&
    (!hideTotalPrice || isTotalOfferPriceApply)
  ) {
    keysToRemove = [...DEFAULT_OFFER_KEYS, ...EXTRA_PRICE_KEYS];
  } else if (sectionName === SearchFilterTypeEnum.PAIRS) {
    if (!hasAnyOfferDetails) {
      keysToRemove = [...DEFAULT_OFFER_KEYS];
    }
    keysToRemove = [...keysToRemove, "pair"];
  } else {
    keysToRemove = [...DEFAULT_OFFER_KEYS];
  }

  const hasValidChild = stockItemRequest?.children?.some((child: any) =>
    showRequestItemFromSupplier(child)
  );

  if (
    (request_type === "seller_view" && !hasValidChild) ||
    request_type === "buyer_view"
  ) {
    keysToRemove = [...keysToRemove, "request_item_from_supplier"];
  }

  return keysToRemove;
};

// Returns the ordered list of column "name" keys that will actually be rendered.
// Use the SAME listDataOptions the header uses (customizeColumnParams ??
// productListViewData ?? defaultListDataOptions) so widths and filters stay
// in sync.
export const getVisibleColumnKeys = (
  listDataOptions: any[] | undefined | null,
  params: VisibleColumnsParams
): string[] => {
  if (!Array.isArray(listDataOptions)) return [];
  const keysToRemove = getKeysToRemove(params);
  return listDataOptions
    .filter((item: any) => !keysToRemove.includes(item?.response_key))
    .filter((item: any) =>
      item?.is_visible === undefined ? true : item.is_visible
    )
    .map((item: any) => item?.name)
    .filter((name: any): name is string => typeof name === "string");
};
